import { Wallet, Info, FileDown, Gift, Mail, Users, Building2, Pencil, Check, X } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { fmtBRL, fmtInt, fmtPct, pct } from "@/lib/format";
import { gerarReciboPDF } from "@/lib/pdfReceipt";
import { sendReceipt, upsertMetaVendedor } from "@/lib/api";
import { toast } from "sonner";
import { useState } from "react";
import { useAuth, isAdmin, isVendedora } from "@/lib/auth";

export default function ComissoesCard({
  metrics = [], plans = [], bonusPct = 0, teamBonusPct = 0, teamHit = false, month = "",
  commissionMap = {}, condoConsultorMap = {}, metasMap = {}, onMetaSaved = null,
}) {
  const { user } = useAuth();
  const admin = isAdmin(user);
  const vend = isVendedora(user);
  const showProfit = !vend; // vendedora vê só receita bruta
  const myVendedor = (user?.vendedor_name || "").trim();
  // Vendedora só vê sua própria linha; comissões dos colegas ficam ocultas.
  const filteredMetrics = vend
    ? metrics.filter((m) => (m.nome || "") === myVendedor)
    : metrics;
  const filteredCommissionMap = vend
    ? Object.fromEntries(Object.entries(commissionMap).filter(([k]) => k === myVendedor))
    : commissionMap;
  const filteredCondoMap = vend
    ? Object.fromEntries(Object.entries(condoConsultorMap).filter(([k]) => k === myVendedor))
    : condoConsultorMap;
  const [sending, setSending] = useState(null);
  const [editingMeta, setEditingMeta] = useState(null); // vendedor sendo editado
  const [metaDraft, setMetaDraft] = useState("");
  const [savingMeta, setSavingMeta] = useState(false);

  const startEditMeta = (nome) => {
    setEditingMeta(nome);
    setMetaDraft(String(metasMap[nome] || 0));
  };
  const cancelEditMeta = () => { setEditingMeta(null); setMetaDraft(""); };
  const saveMeta = async (nome) => {
    const val = Number(String(metaDraft).replace(",", "."));
    if (!Number.isFinite(val) || val < 0) { toast.error("Meta inválida"); return; }
    setSavingMeta(true);
    try {
      await upsertMetaVendedor(nome, val);
      toast.success(`Meta de ${nome} salva: R$ ${val.toFixed(2)}`);
      setEditingMeta(null);
      setMetaDraft("");
      if (onMetaSaved) await onMetaSaved();
    } catch (err) {
      console.error("Erro ao salvar meta:", err);
      toast.error("Erro ao salvar meta");
    } finally { setSavingMeta(false); }
  };
  const sorted = [...filteredMetrics].sort((a, b) => (b.comissao_total || 0) - (a.comissao_total || 0));
  const totalReceita = sorted.reduce((s, v) => s + v.receita, 0);
  const totalLucroBruto = sorted.reduce((s, v) => s + (v.lucro_bruto || 0), 0);
  const totalBase = sorted.reduce((s, v) => s + v.comissao, 0);
  const totalBonusInd = sorted.reduce((s, v) => s + (v.bonus || 0), 0);
  const totalBonusTeam = sorted.reduce((s, v) => s + (v.bonus_team || 0), 0);
  const totalGeral = totalBase + totalBonusInd + totalBonusTeam;
  const totalLucroFinal = totalLucroBruto - totalGeral;
  const totalCondoVendas = Object.values(filteredCondoMap).reduce((s, x) => s + (x?.vendas || 0), 0);
  const totalCondoMeta = Object.values(filteredCondoMap).reduce((s, x) => s + (x?.meta || 0), 0);

  const handlePDF = (v) => {
    gerarReciboPDF({ vendedor: v, month, bonusPct });
  };

  const handleEmail = async (v) => {
    setSending(v.nome);
    try {
      const r = await sendReceipt(v.nome, month);
      toast.success(`Recibo enviado para ${r.to}`);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Falha ao enviar recibo");
    } finally { setSending(null); }
  };

  return (
    <div className="rounded-xl border border-border bg-card p-5" data-testid="comissoes-card">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Wallet className="w-4 h-4 text-primary" />
          <h3 className="font-heading font-semibold text-lg">
            {vend ? "Minha comissão" : "Comissões por vendedor"}
          </h3>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Badge variant="outline" className="font-mono">Base: {fmtBRL(totalBase)}</Badge>
          <Badge variant="outline" className="font-mono text-amber-500 border-amber-500/40">
            <Gift className="w-3 h-3 mr-1" /> Indiv.: {fmtBRL(totalBonusInd)}
          </Badge>
          <Badge variant="outline" className={`font-mono ${teamHit ? "text-emerald-500 border-emerald-500/40" : ""}`}>
            <Users className="w-3 h-3 mr-1" /> Coletivo: {fmtBRL(totalBonusTeam)}
          </Badge>
          <Badge className="font-mono">Total: {fmtBRL(totalGeral)}</Badge>
        </div>
      </div>

      {/* Painel dos planos (só valor de venda + lucro líquido) e comissão por vendedor */}
      <div className="mb-4 grid grid-cols-1 lg:grid-cols-2 gap-3">
        <div className="rounded-lg border border-border bg-background p-3">
          <div className="flex items-center gap-1.5 mb-2 text-xs text-muted-foreground">
            <Info className="w-3 h-3" /> Planos ativos · {showProfit ? "valor de venda × lucro líquido unitário" : "valor de venda"}
          </div>
          <div className="grid grid-cols-2 gap-2 text-xs">
            {plans.map((p) => (
              <div key={p.name} className="rounded-md border border-border p-2">
                <div className="font-semibold truncate">{p.name}</div>
                <div className="text-muted-foreground font-mono">Venda: {fmtBRL(p.price)}</div>
                {showProfit && (
                  <div className="text-emerald-500 font-semibold font-mono">Lucro: {fmtBRL(p.lucro_liquido || 0)}</div>
                )}
              </div>
            ))}
          </div>
        </div>
        <div className="rounded-lg border border-border bg-background p-3">
          <div className="flex items-center gap-1.5 mb-2 text-xs text-muted-foreground">
            <Info className="w-3 h-3" /> Comissão personalizada por vendedor · Bônus indiv. {fmtPct(bonusPct * 100)} · Coletivo {fmtPct(teamBonusPct * 100)} {teamHit ? "(aplicado)" : "(pendente)"}
          </div>
          <div className="grid grid-cols-2 gap-2 text-xs">
            {Object.keys(filteredCommissionMap).length === 0 && (
              <div className="col-span-2 text-muted-foreground">
                {vend
                  ? "Sua comissão personalizada ainda não foi configurada."
                  : <>Configure a comissão de cada vendedor em <strong>Usuários → editar</strong>.</>}
              </div>
            )}
            {Object.entries(filteredCommissionMap).map(([nome, pct]) => (
              <div key={nome} className="rounded-md border border-border p-2 flex items-center justify-between gap-2">
                <span className="truncate">{nome}</span>
                <span className="font-semibold text-primary font-mono">{fmtPct((pct || 0) * 100)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {sorted.length === 0 && (
        <div className="text-sm text-muted-foreground">
          {vend
            ? "Você ainda não tem vendas registradas no período — bora prospectar!"
            : "Sem vendas no período."}
        </div>
      )}

      {sorted.length > 0 && (
        <div className="thin-scroll overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead>Vendedor</TableHead>
                <TableHead className="text-right">Vendas</TableHead>
                <TableHead className="text-right">Condomínios</TableHead>
                <TableHead className="text-right">Receita</TableHead>
                {showProfit && <TableHead className="text-right">Lucro bruto</TableHead>}
                <TableHead className="text-right">Comissão %</TableHead>
                <TableHead className="text-right">Comissão base</TableHead>
                <TableHead className="text-right">Bônus</TableHead>
                <TableHead className="text-right">Total comissão</TableHead>
                {showProfit && <TableHead className="text-right">Lucro final</TableHead>}
                <TableHead className="text-right">Recibo</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sorted.map((v) => {
                const condo = condoConsultorMap[v.nome];
                const condoVendas = condo?.vendas || 0;
                const condoMeta = condo?.meta || 0;
                const condoCount = condo?.condominios || 0;
                const condoPct = pct(condoVendas, condoMeta);
                const condoTone = condoPct >= 100 ? "text-emerald-500" : condoPct >= 50 ? "text-amber-500" : "text-red-500";
                return (
                <TableRow key={v.nome} data-testid={`comissao-row-${v.nome}`}>
                  <TableCell className="font-medium">
                    <div className="flex items-center gap-2 flex-wrap">
                      {v.nome}
                      {v.bateu_meta && (
                        <Badge variant="outline" className="bg-emerald-500/15 text-emerald-500 border-emerald-500/30 text-[10px]">
                          Meta batida
                        </Badge>
                      )}
                      {admin && (
                        editingMeta === v.nome ? (
                          <div className="flex items-center gap-1" data-testid={`edit-meta-${v.nome}`}>
                            <Input
                              type="number" step="10" min="0"
                              value={metaDraft}
                              onChange={(e) => setMetaDraft(e.target.value)}
                              className="h-6 w-24 text-xs px-1"
                              autoFocus
                              onKeyDown={(e) => { if (e.key === "Enter") saveMeta(v.nome); if (e.key === "Escape") cancelEditMeta(); }}
                            />
                            <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => saveMeta(v.nome)} disabled={savingMeta} data-testid={`save-meta-${v.nome}`}>
                              <Check className="w-3 h-3 text-emerald-500" />
                            </Button>
                            <Button size="icon" variant="ghost" className="h-6 w-6" onClick={cancelEditMeta}>
                              <X className="w-3 h-3 text-muted-foreground" />
                            </Button>
                          </div>
                        ) : (
                          <button
                            type="button"
                            onClick={() => startEditMeta(v.nome)}
                            data-testid={`btn-edit-meta-${v.nome}`}
                            className="inline-flex items-center gap-1 rounded-full border border-border px-2 py-0.5 text-[10px] text-muted-foreground hover:border-primary/60 hover:text-primary transition-colors"
                            title="Editar meta mensal"
                          >
                            <Pencil className="w-2.5 h-2.5" />
                            Meta: {metasMap[v.nome] > 0 ? fmtBRL(metasMap[v.nome]) : "—"}
                          </button>
                        )
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-right tabular-nums">{fmtInt(v.vendas)}</TableCell>
                  <TableCell className="text-right tabular-nums" data-testid={`condo-vendas-${v.nome}`}>
                    {condoCount === 0 ? (
                      <span className="text-xs text-muted-foreground">—</span>
                    ) : (
                      <div className="inline-flex flex-col items-end leading-tight">
                        <span className={`font-mono font-semibold ${condoTone}`}>
                          {fmtInt(condoVendas)}{condoMeta > 0 ? `/${fmtInt(condoMeta)}` : ""}
                        </span>
                        <span className="text-[10px] text-muted-foreground inline-flex items-center gap-1">
                          <Building2 className="w-2.5 h-2.5" /> {condoCount} prédio{condoCount > 1 ? "s" : ""}
                        </span>
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="text-right tabular-nums font-mono text-muted-foreground">{fmtBRL(v.receita)}</TableCell>
                  {showProfit && (
                    <TableCell className="text-right tabular-nums font-mono text-emerald-500">{fmtBRL(v.lucro_bruto || 0)}</TableCell>
                  )}
                  <TableCell className="text-right tabular-nums font-mono text-primary">{fmtPct((v.comissao_pct || 0) * 100)}</TableCell>
                  <TableCell className="text-right tabular-nums font-mono">{fmtBRL(v.comissao)}</TableCell>
                  <TableCell className="text-right tabular-nums font-mono text-amber-500">
                    {(v.bonus + v.bonus_team) > 0 ? fmtBRL(v.bonus + v.bonus_team) : "—"}
                  </TableCell>
                  <TableCell className="text-right tabular-nums font-mono font-semibold text-amber-500">
                    {fmtBRL(v.comissao_total)}
                  </TableCell>
                  {showProfit && (
                    <TableCell className={`text-right tabular-nums font-mono font-semibold ${(v.lucro_liquido_final || 0) >= 0 ? "text-emerald-500" : "text-red-500"}`}>
                      {fmtBRL(v.lucro_liquido_final || 0)}
                    </TableCell>
                  )}
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button size="sm" variant="outline" onClick={() => handlePDF(v)} data-testid={`btn-recibo-${v.nome}`}>
                        <FileDown className="w-3.5 h-3.5 mr-1.5" /> PDF
                      </Button>
                      {admin && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleEmail(v)}
                          disabled={sending === v.nome}
                          data-testid={`btn-email-${v.nome}`}
                        >
                          <Mail className="w-3.5 h-3.5 mr-1.5" /> {sending === v.nome ? "Enviando…" : "Enviar"}
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
                );
              })}
              <TableRow className="border-t-2 border-border font-semibold bg-muted/40">
                <TableCell>Total</TableCell>
                <TableCell className="text-right tabular-nums">
                  {fmtInt(sorted.reduce((s, v) => s + v.vendas, 0))}
                </TableCell>
                <TableCell className="text-right tabular-nums font-mono">
                  {totalCondoVendas > 0 ? `${fmtInt(totalCondoVendas)}${totalCondoMeta > 0 ? "/" + fmtInt(totalCondoMeta) : ""}` : "—"}
                </TableCell>
                <TableCell className="text-right tabular-nums font-mono">{fmtBRL(totalReceita)}</TableCell>
                {showProfit && (
                  <TableCell className="text-right tabular-nums font-mono text-emerald-500">{fmtBRL(totalLucroBruto)}</TableCell>
                )}
                <TableCell></TableCell>
                <TableCell className="text-right tabular-nums font-mono">{fmtBRL(totalBase)}</TableCell>
                <TableCell className="text-right tabular-nums font-mono text-amber-500">{fmtBRL(totalBonusInd + totalBonusTeam)}</TableCell>
                <TableCell className="text-right tabular-nums font-mono text-amber-500">{fmtBRL(totalGeral)}</TableCell>
                {showProfit && (
                  <TableCell className={`text-right tabular-nums font-mono ${totalLucroFinal >= 0 ? "text-emerald-500" : "text-red-500"}`}>
                    {fmtBRL(totalLucroFinal)}
                  </TableCell>
                )}
                <TableCell></TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
